import React, { useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Alert, Spinner, Button } from 'react-bootstrap'
import * as pbi from 'powerbi-client'
import { faExpand } from '@fortawesome/free-solid-svg-icons'
import DatumLayout from 'datum/layout'
import { ActionButton } from 'shared/buttons'
import { useDatumReportEmbed } from 'api/hooks/use-datum-reports'

const powerbi = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
)

const DatumReportShow = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const containerRef = useRef(null)
  const reportRef = useRef(null)

  const { data: embedData, isLoading, error } = useDatumReportEmbed({ id })

  const embedReport = useCallback(() => {
    if (!embedData || !containerRef.current) return

    // Reset any previously embedded report
    if (reportRef.current) {
      powerbi.reset(containerRef.current)
      reportRef.current = null
    }

    const config = {
      type: 'report',
      id: embedData.report.id,
      embedUrl: embedData.embed_url,
      accessToken: embedData.embed_token,
      tokenType: pbi.models.TokenType.Embed,
      settings: {
        panes: {
          filters: { expanded: false, visible: true },
          pageNavigation: { visible: true, position: pbi.models.PageNavigationPosition.Left }
        },
        background: pbi.models.BackgroundType.Default
      }
    }

    try {
      const report = powerbi.embed(containerRef.current, config)
      reportRef.current = report

      report.on('error', (event) => {
        console.error('Power BI embed error:', event.detail)
      })
    } catch (embedError) {
      console.error('Failed to embed Power BI report:', embedError)
    }
  }, [embedData])

  useEffect(() => {
    embedReport()

    return () => {
      if (reportRef.current && containerRef.current) {
        try {
          powerbi.reset(containerRef.current)
        } catch (e) {
          // Ignore cleanup errors
        }
        reportRef.current = null
      }
    }
  }, [embedReport])

  const enterFullscreen = () => reportRef.current?.fullscreen()

  const reportName = embedData?.report?.name || 'Report'

  return (
    <DatumLayout title={reportName}>
      <div className="d-flex align-items-center mb-3 gap-3">
        <Button
          variant="light"
          className="text-primary"
          size="sm"
          onClick={() => navigate('/datum/reports')}
        >
          Back to Reports
        </Button>
        {!isLoading && !error && (
          <>
            <h5 className="mb-0">{reportName}</h5>
            <ActionButton
              variant="light"
              className="text-primary ms-auto"
              icon={faExpand}
              label="Full screen"
              onClick={enterFullscreen}
            />
          </>
        )}
      </div>

      {isLoading && (
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading report...</span>
          </Spinner>
          <p className="mt-2 text-muted">Loading report...</p>
        </div>
      )}

      {error && (
        <Alert variant="danger">
          Failed to load the report. Please go back and try again.
        </Alert>
      )}

      {!isLoading && !error && (
        <div
          ref={containerRef}
          className="power-bi-report-container"
          style={{ height: 'calc(100vh - 140px)', width: '100%', border: 'none' }}
        />
      )}
    </DatumLayout>
  )
}

export default DatumReportShow
